import { FC, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';
import {
  createBemBlockBuilder,
  InPageNotification,
  Loading,
} from '@ebsco-ui/ebsco-ui';

import { AuthPageWrapper } from '@app/components';
import { useQueryParams } from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { ROUTE } from '@app/constants';

import {
  useResetPinStep2Mutation,
  useValidateBarcodeMutation,
} from '../resetPinMutations';
import { ResetPinSuccess } from '../ResetPinSuccess';
import { EnterNewPinForm } from './EnterNewPinForm';

import '@app/components/AuthPageWrapper/AuthPageWrapper.scss';

const cnBem = createBemBlockBuilder(['authPageWrapper']);

export const EnterNewPinPage: FC = () => {
  const { $t } = useIntl();
  const queryParams = useQueryParams();
  const resetPinStep2Mutation = useResetPinStep2Mutation();
  const validateBarcodeMutation = useValidateBarcodeMutation();
  const notification = useMemo(
    () =>
      resetPinStep2Mutation.isError
        ? {
            variant: InPageNotification.VARIANT.error,
            header: $t(sharedMessages.somethingWentWrong),
            description: $t(sharedMessages.weAreNotAbleToProcessRequest),
          }
        : undefined,
    [resetPinStep2Mutation.isError, $t]
  );

  const validateBarcode = validateBarcodeMutation.mutate;

  useEffect(() => {
    validateBarcode(queryParams.token as string);
  }, [queryParams.token, validateBarcode]);

  const handleSubmit = (pin: string) => {
    resetPinStep2Mutation.mutate({
      pin,
      token: queryParams.token as string,
    });
  };

  const renderContent = () => {
    if (validateBarcodeMutation.isError) {
      return <Navigate to={ROUTE.notFound} />;
    }

    if (!validateBarcodeMutation.isSuccess) {
      return (
        <div data-auto="spinner">
          <Loading loadingMessage={$t(sharedMessages.loadingMessage)} />
        </div>
      );
    }

    if (resetPinStep2Mutation.isSuccess) {
      return <ResetPinSuccess />;
    }

    return (
      <EnterNewPinForm
        isLoading={resetPinStep2Mutation.isLoading}
        onSubmit={handleSubmit}
      />
    );
  };

  return (
    <>
      <Helmet>
        <title>
          {$t({
            id: 'resetPin.documentTitle',
            defaultMessage: 'Reset PIN - EBSCO Locate',
          })}
        </title>
      </Helmet>
      <AuthPageWrapper
        title={$t(sharedMessages.resetPin)}
        containerClassName={cnBem('__resetPinContainer')}
        notification={notification}
      >
        {renderContent()}
      </AuthPageWrapper>
    </>
  );
};
